import {
  ClipboardList,
  FileEdit,
  LayoutGrid,
  Presentation,
  Settings,
  LogOut,
} from 'lucide-react'
import type React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { cn } from '../utils/cn'
import { resolveBackendUrl } from '@/utils/media'
import eyeLogo from '@/assets/Landing/eyelogo.svg'

interface NavItem {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  href: string
}

const navItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutGrid,
    href: '/dashboard',
  },
  {
    id: 'assessment',
    label: 'Assessment',
    icon: FileEdit,
    href: '/assessment',
  },
  {
    id: 'results',
    label: 'Results',
    icon: ClipboardList,
    href: '/results',
  },
  {
    id: 'learning-path',
    label: 'Learning Path',
    icon: Presentation,
    href: '/learning-path',
  },
  // {
  //   id: 'therapists',
  //   label: 'Therapists',
  //   icon: Users,
  //   href: '/therapists',
  // },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    href: '/settings',
  },
]

interface IPVPSidebarProps {
  activeItem?: string | undefined
  organizationName?: string | undefined
  organizationLogo?: string | undefined
  className?: string | undefined
}

export function SidebarContent({
  organizationName,
  organizationLogo,
}: IPVPSidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, user } = useAuth()

  // Use the organization name from user profile if not provided as prop
  const displayOrganizationName =
    organizationName ||
    (user as { organizationName?: string })?.organizationName ||
    (user as { organization?: { name?: string } })?.organization?.name ||
    (user as { coupleOrganizationName?: string })?.coupleOrganizationName ||
    'IRIS Dashboard'

  const orgLogoUrl = organizationLogo
    || resolveBackendUrl((user as { coupleOrganizationLogo?: string | null })?.coupleOrganizationLogo)
    || ''

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='flex h-full flex-col bg-[#F8F9FA]'>
      {/* Header */}
      <div className='px-6 py-6'>
        <div className='flex items-center gap-2'>
          <img src={eyeLogo} alt='IRIS Logo' className='h-8 w-8' />
          <span className='text-[24px] font-bold text-[#151515]'>IRIS</span>
        </div>
        <p className='text-[10px] text-[#4e4d4d] mt-1'>Intimate Relationship Indicators Screening</p>
      </div>

      {/* Organization Card */}
      <div className='mx-4 mb-6'>
        <div className='flex items-center gap-3 rounded-[12px] bg-white px-4 py-4'>
          {orgLogoUrl ? (
            <img
              src={orgLogoUrl}
              alt={displayOrganizationName}
              className='h-10 w-10 rounded-full object-cover flex-shrink-0'
            />
          ) : (
            <div className='h-10 w-10 rounded-full bg-[#F3F4F6] flex items-center justify-center flex-shrink-0'>
              <span className='text-[14px] font-bold text-[#4e4d4d]'>
                {displayOrganizationName.charAt(0)}
              </span>
            </div>
          )}
          <p className='text-[14px] font-semibold text-[#151515] leading-tight'>
            {displayOrganizationName}
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className='flex-1 px-2'>
        {navItems.map(item => {
          const Icon = item.icon
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')

          return (
            <a
              key={item.id}
              href={item.href}
              className={cn(
                'relative flex items-center gap-3 px-4 py-3 text-[14px] font-medium transition-colors',
                isActive
                  ? 'text-[#FA6017]'
                  : 'text-[#4e4d4d] hover:text-[#151515]'
              )}
            >
              {/* Active indicator - orange bar on left edge */}
              {isActive && (
                <div className='absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[24px] bg-[#FA6017] rounded-r-full' />
              )}

              {/* Icon */}
              <Icon
                className={cn(
                  'h-5 w-5',
                  isActive ? 'text-[#FA6017]' : 'text-[#4e4d4d]'
                )}
              />

              {/* Label */}
              <span>{item.label}</span>
            </a>
          )
        })}
      </nav>

      {/* Logout Button */}
      <div className='px-4 py-4 border-t border-[#E5E7EB]'>
        <button
          onClick={handleLogout}
          className='w-full flex items-center gap-3 px-4 py-3 text-[14px] font-medium text-[#EF4444] hover:bg-red-50 rounded-lg transition-colors'
        >
          <LogOut className='h-5 w-5' />
          <span>Logout</span>
        </button>
      </div>
    </div>
  )
}

export function IPVPSidebar({
  activeItem,
  organizationName,
  organizationLogo,
  className,
}: IPVPSidebarProps) {
  return (
    <aside className={cn('hidden h-screen w-[280px] lg:block border-r border-[#E5E7EB]', className)}>
      <SidebarContent
        activeItem={activeItem}
        organizationName={organizationName}
        organizationLogo={organizationLogo}
      />
    </aside>
  )
}
